// =============================================================================
// src/lib/tasksApi.js - data access for the Tasks tab (Oct 2026 redesign).
//
// Reads go through tasks_list_v (tasks + patient + assignee + note count +
// search_text; security_invoker so the caller's RLS applies). Writes go to
// tasks through updateRow / insertRow so the audit trail is unchanged.
//
//   fetchTaskList({ scope, uid, role, filters, sort, limit, offset })
//   fetchScopeCounts({ uid, role })
//   fetchTaskById(id)
//   fetchTaskActivity(taskId)      notes (get_task_notes) + task_events
//   updateTask(id, patch)          returns the updated row
//   createTask(row, practiceId)
//   claimTask(id)                  task_claim RPC
//   addTaskNote(taskId, text)      add_task_note RPC
//   fetchWorkload()                task_workload RPC
//   fetchStaff()                   active staff for the assignee picker
//
// Migration 20261001180000_tasks_redesign.
// =============================================================================
import { supabase } from "./supabaseClient";
import { insertRow, updateRow } from "./db";

export const OPEN_STATUSES = ["Open", "In Progress"];
export const DONE_STATUSES = ["Completed", "Cancelled"];

const LIST_COLUMNS =
  "id, practice_id, title, description, category, priority, status, patient_id, appointment_id, assigned_to, assigned_role, " +
  "due_date, source, related_entity, related_id, completed_at, completed_by, created_by, created_at, updated_at, " +
  "snoozed_until, waiting_on, waiting_note, waiting_since, checklist, recurrence, recurrence_parent_id, " +
  "patient_first_name, patient_last_name, patient_dob, patient_mrn, patient_phone, patient_gender, " +
  "assignee_name, assignee_role, note_count";

// Today / tomorrow / end of week as YYYY-MM-DD in the browser's time zone.
export function isoDay(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

// PostgREST or() values with spaces (role names) must be double quoted.
const q = (v) => '"' + String(v).replace(/"/g, "") + '"';

export function mineFilter(uid, role) {
  return role
    ? "assigned_to.eq." + uid + ",and(assigned_to.is.null,assigned_role.eq." + q(role) + ")"
    : "assigned_to.eq." + uid;
}

function applyScope(query, scope, uid, role) {
  const nowIso = new Date().toISOString();
  if (scope === "done") return query.in("status", DONE_STATUSES);
  query = query.in("status", OPEN_STATUSES);
  if (scope === "snoozed") return query.gt("snoozed_until", nowIso);
  query = query.or("snoozed_until.is.null,snoozed_until.lte." + nowIso);
  if (scope === "mine") return query.or(mineFilter(uid, role));
  if (scope === "unassigned") return query.is("assigned_to", null);
  if (scope === "waiting") return query.not("waiting_on", "is", null);
  return query; // team: everything open in the practice
}

function applyFilters(query, f) {
  if (!f) return query;
  const text = String(f.search || "").trim().toLowerCase();
  if (text) query = query.ilike("search_text", "%" + text.replace(/[%_]/g, " ") + "%");
  if (f.category) query = query.eq("category", f.category);
  if (f.priority === "High+") query = query.in("priority", ["High", "Urgent"]);
  else if (f.priority) query = query.eq("priority", f.priority);
  if (f.source) query = f.source === "(none)" ? query.is("source", null) : query.ilike("source", f.source);
  if (f.assignee === "(unassigned)") query = query.is("assigned_to", null);
  else if (f.assignee && f.assignee.startsWith("role:")) query = query.is("assigned_to", null).eq("assigned_role", f.assignee.slice(5));
  else if (f.assignee) query = query.eq("assigned_to", f.assignee);
  if (f.patientId) query = query.eq("patient_id", f.patientId);
  if (f.due === "overdue") query = query.lt("due_date", isoDay(0));
  else if (f.due === "today") query = query.eq("due_date", isoDay(0));
  else if (f.due === "week") query = query.lte("due_date", isoDay(7));
  else if (f.due === "none") query = query.is("due_date", null);
  return query;
}

function applySort(query, sort, scope) {
  if (scope === "done") return query.order("completed_at", { ascending: false, nullsFirst: false }).order("updated_at", { ascending: false });
  if (sort === "updated") return query.order("updated_at", { ascending: false });
  if (sort === "created") return query.order("created_at", { ascending: false });
  // "due" and "priority" are ordered by due date on the server; priority is
  // re-sorted on the page (enum order is not alphabetical).
  return query.order("due_date", { ascending: true, nullsFirst: false }).order("created_at", { ascending: true });
}

export async function fetchTaskList({ scope, uid, role, filters, sort, limit = 200, offset = 0 }) {
  let query = supabase.from("tasks_list_v").select(LIST_COLUMNS, { count: "exact" });
  query = applyScope(query, scope, uid, role);
  query = applyFilters(query, filters);
  query = applySort(query, sort, scope).range(offset, offset + limit - 1);
  const { data, error, count } = await query;
  if (error) throw error;
  return { rows: data || [], total: count ?? (data || []).length };
}

// Badge counts for the scope tabs.
export async function fetchScopeCounts({ uid, role }) {
  const nowIso = new Date().toISOString();
  const base = () => supabase.from("tasks").select("id", { count: "exact", head: true }).in("status", OPEN_STATUSES);
  const notSnoozed = (qq) => qq.or("snoozed_until.is.null,snoozed_until.lte." + nowIso);
  const [mine, unassigned, waiting, snoozed] = await Promise.all([
    notSnoozed(base()).or(mineFilter(uid, role)),
    notSnoozed(base()).is("assigned_to", null),
    notSnoozed(base()).not("waiting_on", "is", null),
    base().gt("snoozed_until", nowIso),
  ]);
  return {
    mine: mine.count || 0,
    unassigned: unassigned.count || 0,
    waiting: waiting.count || 0,
    snoozed: snoozed.count || 0,
  };
}

export async function fetchTaskById(id) {
  const { data, error } = await supabase.from("tasks_list_v").select(LIST_COLUMNS).eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}

// Notes and history merged into one timeline (oldest first).
export async function fetchTaskActivity(taskId) {
  const [notes, events] = await Promise.all([
    supabase.rpc("get_task_notes", { p_task_id: taskId }),
    supabase.from("task_events").select("id, kind, detail, actor_user_id, created_at").eq("task_id", taskId).order("created_at"),
  ]);
  if (notes.error) throw notes.error;
  if (events.error) throw events.error;
  const items = [
    ...(notes.data || []).map((n) => ({ type: "note", id: "n" + n.id, at: n.created_at, note: n })),
    ...(events.data || []).map((e) => ({ type: "event", id: "e" + e.id, at: e.created_at, event: e })),
  ];
  items.sort((a, b) => String(a.at).localeCompare(String(b.at)));
  return items;
}

export async function updateTask(id, patch) {
  return updateRow("tasks", id, patch, { audit: { entityType: "tasks" } });
}

export async function createTask(row, practiceId) {
  return insertRow("tasks", row, practiceId, { audit: { entityType: "tasks" } });
}

export async function claimTask(id) {
  const { error } = await supabase.rpc("task_claim", { p_task_id: id });
  if (error) throw error;
}

export async function addTaskNote(taskId, text) {
  const { error } = await supabase.rpc("add_task_note", { p_task_id: taskId, p_note: text });
  if (error) throw error;
}

export async function fetchWorkload() {
  const { data, error } = await supabase.rpc("task_workload");
  if (error) throw error;
  return data || [];
}

export async function fetchStaff() {
  const { data, error } = await supabase.from("users").select("id, full_name, role").eq("is_active", true).order("full_name");
  if (error) throw error;
  return (data || []).filter((u) => u.role !== "Patient" && u.role !== "System");
}
